import React, { createContext, useContext } from 'react';

/**
 * AuthContext
 * 
 * From window.LEP (set by wp_localize_script):
 * - officer_id: Current officer ID (integer)
 * - is_admin: Whether current user is admin (boolean)
 * - session_token: For Safari compatibility (mobile check-ins)
 * - logout_url: Custom officer logout URL (?lep_logout=1 with nonce)
 * - nonce: WordPress nonce for AJAX validation
 * 
 * Does NOT manage authentication state; that's handled in PHP.
 * This just passes through the initial authenticated state to React.
 */
const AuthContext = createContext();

export function AuthProvider({ children, authState }) {
  const fallbackAdminTabs = window.LEP?.pro_unlocked
    ? ['general', 'officers-categories', 'patrols', 'appearance', 'documentation', 'change-requests', 'api-views', 'logs']
    : ['general', 'officers-categories', 'patrols', 'appearance', 'documentation'];

  // Session-specific fields come from authState (fetched via lep_get_auth_state AJAX).
  // Static config (pro_unlocked, allowed_admin_tabs, etc.) comes from window.LEP.
  const auth = {
    officer_id:                authState?.officer_id ?? null,
    is_admin:                  authState?.is_admin ?? false,
    nonce:                     authState?.nonce ?? '',
    logout_url:                authState?.logout_url ?? '',
    session_token:             authState?.session_token ?? '',
    has_view_stats_permission: authState?.has_view_stats_permission ?? false,
    can_view_department_stats: authState?.can_view_department_stats ?? false,
    // Static fields from baked window.LEP (safe to cache)
    pro_unlocked:              !!window.LEP?.pro_unlocked,
    allowed_admin_tabs:        Array.isArray(window.LEP?.allowed_admin_tabs) ? window.LEP.allowed_admin_tabs : fallbackAdminTabs,
    isAuthenticated:           !!(authState?.officer_id),
  };

  if (!auth.isAuthenticated) {
    throw new Error('LEP Portal: Not authenticated. Please log in via /lep-login/');
  }

  return (
    <AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
